import { Injectable, inject } from '@angular/core';

import { CompetitionRouteService } from '@frontend/sports/competition-list/feature';
import { EventGroup, LeagueGroup } from '@frontend/sports/grid/core/feature/model';
import { GridActions, IGridRootState } from '@frontend/sports/grid/core/feature/store';
import { NOT_APPLICABLE, TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { find } from 'lodash-es';
import { concatMap, map, of, tap, withLatestFrom } from 'rxjs';

/* Disable type definitions for the epics, because it will be changed in redux-observable v1.0.0 */
@Injectable({ providedIn: 'root' })
export class CompetitionGridTrackingEffects {
    private trackingService = inject(TrackingService);
    private route = inject(CompetitionRouteService);
    private actions$ = inject(Actions);
    private store = inject<Store<IGridRootState>>(Store);

    onSetGroupCollapse$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(GridActions.setGroupCollapse),
                concatMap((action) => of(action).pipe(withLatestFrom(this.store.pipe(map((state) => state.grid[action.payload.id]))))),
                tap(([action, grid]) => {
                    const activeGroup = grid.groups.find((group) => group.id === action.payload.groupId);

                    if (activeGroup) {
                        const posGroup = grid.groups.indexOf(activeGroup);
                        const route = this.route.params();

                        this.trackingService.track(trackingConstants.EVENT_CLICKS, {
                            [trackingConstants.COMPONENT_CATEGORY_EVENT]: 'Competition Page Interactions',
                            [trackingConstants.COMPONENT_LABEL_EVENT]: `Competition_${route.marketOffer}`,
                            [trackingConstants.COMPONENT_ACTION_EVENT]: activeGroup.collapsed ? 'Minimise' : 'Maximise',
                            [trackingConstants.COMPONENT_POSITION_EVENT]: posGroup.toString(),
                            [trackingConstants.COMPONENT_LOCATION_EVENT]: NOT_APPLICABLE,
                            [trackingConstants.COMPONENT_EVENT_DETAILS]: activeGroup.name,
                        });
                    }
                }),
            );
        },
        { dispatch: false },
    );

    onCollapseEvents$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(GridActions.collapseEvents),
                concatMap((action) => of(action).pipe(withLatestFrom(this.store.pipe(map((state) => state.grid[action.payload.id]))))),
                tap(([action, grid]) => {
                    const activeEvent = find(grid.groups, (group) => find(group.events, (event) => event.id === action.payload.eventId)) as
                        | EventGroup
                        | LeagueGroup
                        | undefined;

                    if (activeEvent) {
                        this.trackingService.track(trackingConstants.EVENT_CLICKS, {
                            [trackingConstants.COMPONENT_CATEGORY_EVENT]: 'Competition Page Interactions',
                            [trackingConstants.COMPONENT_LABEL_EVENT]: 'Event',
                            [trackingConstants.COMPONENT_ACTION_EVENT]: activeEvent.collapsed ? 'Minimise' : 'Maximise',
                            [trackingConstants.COMPONENT_POSITION_EVENT]: grid.groups.findIndex((i) => i.id === activeEvent.id).toString(),
                            [trackingConstants.COMPONENT_LOCATION_EVENT]: NOT_APPLICABLE,
                        });
                    }
                }),
            );
        },
        { dispatch: false },
    );
}
